export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  customerId: string;
};

export type UpsertUser = Omit<User, 'id'>;

import { isTestMode } from './dataService';
import api from './api';

function buildDummyUsers(): User[] {
  const first = ['Max', 'Julia', 'Anna', 'Lukas', 'Sven', 'Mara', 'Tom', 'Lea', 'Paul', 'Lena'];
  const last = ['Mustermann', 'Schmidt', 'Meyer', 'Fischer', 'Weber', 'Wagner', 'Becker', 'Hoffmann', 'Schäfer', 'Koch'];
  return Array.from({ length: 12 }, (_, i) => ({
    id: `usr-${i + 1}`,
    firstName: first[i % first.length],
    lastName: last[i % last.length],
    email: `user${i + 1}@example.com`,
    customerId: `cust-${(i % 5) + 1}`,
  }));
}

export async function listUsers(params?: { q?: string }) {
  if (isTestMode()) {
    let items = buildDummyUsers();
    const q = (params?.q || '').toLowerCase();
    if (q) items = items.filter(u => `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    return Promise.resolve({ items, total: items.length });
  }
  try {
    const res = await api.get('/users', { params });
    return res.data as { items: User[]; total: number };
  } catch {
    // Fallback to dummy if backend unreachable
    let items = buildDummyUsers();
    const q = (params?.q || '').toLowerCase();
    if (q) items = items.filter(u => `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    return { items, total: items.length };
  }
}

export async function createUser(payload: UpsertUser) {
  if (isTestMode()) {
    const id = `usr-${Math.floor(Math.random() * 100000)}`;
    return Promise.resolve({ id, ...payload });
  }
  try {
    const res = await api.post('/users', payload);
    return res.data as User;
  } catch {
    const id = `usr-${Math.floor(Math.random() * 100000)}`;
    return { id, ...payload };
  }
}

export async function deleteUser(id: string) {
  if (isTestMode()) {
    return Promise.resolve();
  }
  try {
    await api.delete(`/users/${id}`);
  } catch {
    // treat as success in offline/test-like scenarios
    return;
  }
}

export async function updateUser(id: string, payload: Partial<UpsertUser>) {
  if (isTestMode()) {
    return Promise.resolve({ id, firstName: payload.firstName || 'Updated', lastName: payload.lastName || 'User', email: payload.email || 'updated@example.com', customerId: payload.customerId || 'cust-1' });
  }
  try {
    const res = await api.patch(`/users/${id}`, payload);
    return res.data as User;
  } catch {
    return { id, firstName: payload.firstName || 'Updated', lastName: payload.lastName || 'User', email: payload.email || 'updated@example.com', customerId: payload.customerId || 'cust-1' };
  }
}
